import { useState, useEffect } from 'react';

import { fetchAdobeTaxonomyData, AdobeTaxonomyData } from 'src/services/http/adobe';

export interface AdobeTaxonomy {
  catalog: string[];
  loType: string[];
  loFormat: string[];
  duration: string[];
  skills: string[];
  skillLevels: string[];
  tags: string[];
  learnerState: string[];
}

export const convertAdobeTaxonomyData = (data: AdobeTaxonomyData): AdobeTaxonomy => ({
  catalog: data.catalog.map((item: { label: string }) => item.label),
  loType: data.loType.map((item: { label: string }) => item.label),
  loFormat: data.loFormat.map((item: { label: string }) => item.label),
  duration: data.duration.map((item: { label: string }) => item.label),
  skills: data.skills?.map((item: { label: string }) => item.label) || [],
  skillLevels: data.skillLevels.map((item: { label: string }) => item.label),
  tags: data.tags?.map((item: { label: string }) => item.label) || [],
  learnerState: data.learnerState.map((item: { label: string }) => item.label),
});

export const adobeTaxonomyToDisplayTextMap: { [key: string]: string } = {
  catalog: 'Catalog',
  loType: 'Type',
  loFormat: 'Format',
  duration: 'Duration',
  skills: 'Skills',
  skillLevels: 'Skill Levels',
  tags: 'Tags',
  learnerState: 'Status',
};

interface UseAdobeTaxonomyReturn {
  adobeTaxonomy: AdobeTaxonomy;
  catalogMapping: { [label: string]: string };
  isLoading: boolean;
  error: boolean;
}

export const useAdobeTaxonomy = (isAdobeCatalog: boolean): UseAdobeTaxonomyReturn => {
  const [adobeTaxonomy, setAdobeTaxonomy] = useState<AdobeTaxonomy>({} as AdobeTaxonomy);
  const [catalogMapping, setCatalogMapping] = useState<{ [label: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isAdobeCatalog) {
      setIsLoading(true);
      setError(false);

      fetchAdobeTaxonomyData()
        .then((data) => {
          setAdobeTaxonomy(convertAdobeTaxonomyData(data));

          const mapping: { [label: string]: string } = {};

          data.catalog.forEach((item) => {
            mapping[item.label] = item.value;
          });
          setCatalogMapping(mapping);
          setError(false);
        })
        .catch(() => {
          setAdobeTaxonomy({} as AdobeTaxonomy);
          setCatalogMapping({});
          setError(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setAdobeTaxonomy({} as AdobeTaxonomy);
      setCatalogMapping({});
      setIsLoading(false);
      setError(false);
    }
  }, [isAdobeCatalog]);

  return {
    adobeTaxonomy,
    catalogMapping,
    isLoading,
    error,
  };
};
